import React from "react";
import { motion } from "framer-motion";

const markets = [
  { city: "Kochi", role: "Headquarters", tz: "GMT+5:30", flag: "🇮🇳" },
  { city: "Dubai", role: "Gulf Corporate Desk", tz: "GMT+4", flag: "🇦🇪" },
  { city: "Doha", role: "Qatar Operations", tz: "GMT+3", flag: "🇶🇦" },
  { city: "Riyadh", role: "KSA Access", tz: "GMT+3", flag: "🇸🇦" },
  { city: "Singapore", role: "APAC Logistics", tz: "GMT+8", flag: "🇸🇬" },
  { city: "London", role: "EMEA Partnerships", tz: "GMT+0", flag: "🇬🇧" },
];

const verticals = [
  {
    k: "01",
    t: "Premium Brand & Luxury",
    b: "Jewellery maisons, hospitality groups, heritage retail — editorial narrative that commands shelf, pricing and press.",
  },
  {
    k: "02",
    t: "Education & Institutions",
    b: "Colleges, universities and training academies — admissions marketing, leadership profiling and institutional reputation.",
  },
  {
    k: "03",
    t: "Banking & Financial Services",
    b: "Scheduled banks, NBFCs and wealth platforms — trust architecture, regulator-grade communications and crisis readiness.",
  },
  {
    k: "04",
    t: "Coaching & EdTech",
    b: "Test-prep brands and professional academies — enrolment pipelines, authority building and city-level dominance.",
  },
  {
    k: "05",
    t: "Corporate Events",
    b: "Shareholder meets, product launches, investor summits — produced to global standards from brief to teardown.",
  },
  {
    k: "06",
    t: "Dispute & Litigation PR",
    b: "Boardroom disputes, promoter conflicts and regulatory scrutiny — reputation defence that stands up in print and in court.",
  },
  {
    k: "07",
    t: "International Logistics & Shipment",
    b: "A new Narzis vertical — multimodal freight, Gulf–India corridors and port-adjacent trade communications.",
  },
  {
    k: "08",
    t: "Political & Public Affairs",
    b: "Assembly, local body and national campaigns — the discipline that remains at the heart of the house.",
  },
];

const InternationalPractice = () => {
  return (
    <section
      id="international"
      data-testid="international-section"
      className="section-gutter py-16 md:py-28 border-t border-black/10 bg-[#0a0a0a] text-white"
    >
      {/* Header */}
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
        <div className="col-span-12 md:col-span-7">
          <div className="overline text-white/50">Chapter 03 — Global Practice</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl mt-5 md:mt-6 leading-[0.95]">
            Built in Kochi.
            <br />
            <em>Briefed in six capitals.</em>
          </h2>
        </div>
        <div className="col-span-12 md:col-span-4 md:col-start-9 self-end">
          <p className="text-sm md:text-base text-white/70 leading-relaxed">
            Narzis Ventures LLP operates one unified standard across markets —
            from premium Indian brands to Gulf capital flows, European
            partnerships and APAC logistics corridors.
          </p>
        </div>
      </div>

      {/* City Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-l border-t border-white/10 mb-16 md:mb-24">
        {markets.map((m, i) => (
          <motion.div
            key={m.city}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.05 * i }}
            className="border-r border-b border-white/10 p-5 md:p-7"
            data-testid={`market-${i}`}
          >
            <div className="text-2xl md:text-3xl">{m.flag}</div>
            <div className="font-serif-display text-2xl md:text-3xl mt-3 md:mt-4 leading-tight">
              {m.city}
            </div>
            <div className="mt-2 text-[0.65rem] md:text-[0.7rem] uppercase tracking-[0.18em] text-white/55 leading-relaxed">
              {m.role}
            </div>
            <div className="mt-3 text-[0.65rem] uppercase tracking-[0.2em] text-white/35">
              {m.tz}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Verticals */}
      <div className="mb-10 md:mb-12">
        <div className="overline text-white/50 mb-3">The Eight Verticals</div>
        <h3 className="font-serif-display text-3xl md:text-5xl leading-[1.02] max-w-3xl">
          One agency. <em>Eight disciplines.</em> Zero compromise on standard.
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-white/10">
        {verticals.map((v, i) => (
          <motion.div
            key={v.k}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: 0.04 * i }}
            data-testid={`vertical-${i}`}
            className="border-r border-b border-white/10 p-6 md:p-8 hover:bg-white hover:text-black transition-colors group"
          >
            <div className="font-serif-display italic text-3xl text-white/40 group-hover:text-black/40">
              {v.k}
            </div>
            <div className="font-serif-display text-xl md:text-2xl mt-4 leading-snug">
              {v.t}
            </div>
            <p className="mt-4 text-xs md:text-sm leading-relaxed text-white/70 group-hover:text-black/75">
              {v.b}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default InternationalPractice;
